// MapViewCompact.jsx

import React, { useEffect, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { buildMapMarkerOptions } from './utils/mapMarkerOptions';

// Hooks
import useMapPins from './hooks/useMapPins';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import './MapView.css';
import './MapViewCompact.css';

// Components
import { getMapStylesByThemeMode } from './mapThemeStyles';
import { loadGoogleMaps } from 'googleMapsLoader';

const DEFAULT_CENTER = { lat: 37.0902, lng: -95.7129 };
const DEFAULT_ZOOM = 4;

/**
 * MapViewCompact
 *
 * Renders the compact map surface used by dashboard cards.
 *
 * @param {object} typeDef - The type definition used to resolve map pins.
 * @param {array} [nodes=[]] - Nodes that may contribute mappable locations.
 *
 * @returns {ReactNode} The rendered compact map surface.
 */
const MapViewCompact = React.memo(({ typeDef, nodes = [], onItemClick = null }) => {

  // ----- CONTEXT ----- //

  const { theme, themeMode } = useTheme();

  // ----- HOOKS ----- //

  const { openNode } = useNodeOpenIntent();
  const { pins } = useMapPins({ nodes, typeDef });
  const mapStyles = getMapStylesByThemeMode(themeMode);

  // ----- REFS ----- //

  const mapElRef = useRef(null);

  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const listenersRef = useRef([]);

  // ----- STATE ----- //

  const [isReady, setIsReady] = useState(false);
  const [isMapInstanceReady, setIsMapInstanceReady] = useState(false);
  const [loadError, setLoadError] = useState('');

  // ----- EFFECTS ----- //

  useEffect(() => {
    let isMounted = true;

    loadGoogleMaps()
      .then(() => {
        if (!isMounted) return;
        setIsReady(true);
        setLoadError('');
      })
      .catch((error) => {
        if (!isMounted) return;
        setLoadError(error?.message || 'Map unavailable');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isReady || !mapElRef.current) return;
    if (mapRef.current) {
      if (!isMapInstanceReady) setIsMapInstanceReady(true);
      return;
    }

    mapRef.current = new window.google.maps.Map(mapElRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      disableDefaultUI: true,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      zoomControl: false,
      styles: mapStyles,
    });
    setIsMapInstanceReady(true);
  }, [isReady, mapStyles, isMapInstanceReady]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ styles: mapStyles });
  }, [mapStyles]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapInstanceReady) return;

    listenersRef.current.forEach((listener) => listener.remove());
    listenersRef.current = [];

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (!pins.length) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    pins.forEach((pin) => {
      const marker = new window.google.maps.Marker(
        buildMapMarkerOptions({
          pin,
          map,
          google: window.google,
          themeMode,
        }),
      );

      /**
       * handlePinClick
       *
       * Handles pin click.
       */
      const handlePinClick = () => {
        if (!pin?.nodeKey) return;
        if (typeof onItemClick === 'function') {
          onItemClick({ key: pin.nodeKey, title: pin?.title || '' });
          return;
        }
        openNode({ key: pin.nodeKey }, { preferPeek: true });
      };

      const clickListener = marker.addListener('click', handlePinClick);

      listenersRef.current.push(clickListener);
      markersRef.current.push(marker);

      bounds.extend({ lat: pin.latitude, lng: pin.longitude });
    });

    if (pins.length === 1) {
      map.setCenter({ lat: pins[0].latitude, lng: pins[0].longitude });
      map.setZoom(11);
      return;
    }

    map.fitBounds(bounds, 28);
  }, [pins, openNode, onItemClick, themeMode, isMapInstanceReady]);

  useEffect(() => {
    const map = mapRef.current;
    const mapEl = mapElRef.current;

    if (!isReady || !isMapInstanceReady || !map || !mapEl || !window?.ResizeObserver) return;

    const syncViewport = () => {
      const rect = mapEl.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      window.google.maps.event.trigger(map, 'resize');

      if (!pins.length) {
        map.setCenter(DEFAULT_CENTER);
        map.setZoom(DEFAULT_ZOOM);
        return;
      }

      if (pins.length === 1) {
        map.setCenter({ lat: pins[0].latitude, lng: pins[0].longitude });
        map.setZoom(11);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      pins.forEach((pin) => bounds.extend({ lat: pin.latitude, lng: pin.longitude }));
      map.fitBounds(bounds, 28);
    };

    const resizeObserver = new window.ResizeObserver(() => {
      syncViewport();
    });

    resizeObserver.observe(mapEl);
    syncViewport();

    return () => {
      resizeObserver.disconnect();
    };
  }, [isReady, pins, isMapInstanceReady]);

  useEffect(() => () => {
    listenersRef.current.forEach((listener) => listener.remove());
    listenersRef.current = [];
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
  }, []);

  if (loadError) {
    return (
      <div
        className="map-view-compact-fallback"
      >
        {loadError}
      </div>
    );
  }

  if (!isReady) {
    return (
      <div
        className="map-view-compact-fallback"
      >
        Loading map...
      </div>
    );
  }

  // ----- RENDER ----- //

  return (
    <div
      className="map-view-compact-container"
      style={{
        border: `1px solid ${theme.backgroundMuted}`,
      }}
    >

      {/* COMPACT CANVAS */}
      <div className="map-view-compact-canvas" ref={mapElRef} />

      {/* COMPACT EMPTY OVERLAY */}
      {!pins.length ? (
        <div
          className="map-view-compact-fallback map-view-compact-empty-overlay"
        >
          No mappable locations.
        </div>
      ) : null}
    </div>
  );
});

// Dev Tools
MapViewCompact.displayName = 'MapViewCompact';

export default MapViewCompact;
